import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  uploadIcon,
  deleteIcon,
  getIconManifest,
  getIconUrl,
  checkIconUsage,
  validateSvgFile,
  isValidIconName,
  downloadAllIcons
} from '../../services/iconService';
import { clearCustomIconsCache } from '../../services/iconSearchService';
import IconGenerator from './IconGenerator';
import '../../styles/icons.css';
import '../../styles/icon-clues.css';
import { PUZZLES_COLLECTION } from '../../services/crosswordService';

const PAGE_SIZE = 48;

function CustomIconsManager() {
  const [manifest, setManifest] = useState({});
  const [iconUrls, setIconUrls] = useState({});
  const [iconUsage, setIconUsage] = useState({});
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loadingManifest, setLoadingManifest] = useState(false);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [listLoaded, setListLoaded] = useState(false); // list is fetched on demand to save Firestore reads
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(null); // { files: [File], iconNames: [string] }
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');

  const iconNames = Object.keys(manifest).sort();
  const filteredNames = search
    ? iconNames.filter(name => name.includes(search.trim().toLowerCase()))
    : iconNames;
  const totalPages = Math.max(1, Math.ceil(filteredNames.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageNames = filteredNames.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const pageKey = pageNames.join(',');

  // Only fetch download URLs for icons on the visible page
  useEffect(() => {
    let cancelled = false;
    const missing = pageNames.filter(name => !iconUrls[name]);
    if (missing.length === 0) return;

    Promise.all(missing.map(async name => {
      try {
        return [name, await getIconUrl(name)];
      } catch (err) {
        console.error(`Failed to load URL for ${name}:`, err);
        return null;
      }
    })).then(results => {
      if (cancelled) return;
      const loaded = Object.fromEntries(results.filter(Boolean));
      setIconUrls(prev => ({ ...prev, ...loaded }));
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, manifest]);

  // Refresh after changes, but only if the list has been opened
  async function refreshList() {
    if (listLoaded) await loadData();
  }

  async function loadData() {
    setListLoaded(true);
    setLoadingManifest(true);
    setLoadingUsage(true);
    setError(null);

    try {
      // Load manifest first (fast)
      const manifestData = await getIconManifest();
      // Drop cached URLs so replaced icons get fresh download tokens
      setIconUrls({});
      setManifest(manifestData);
      setLoadingManifest(false);

      // Load icon usage in the background (slowest)
      loadIconUsage();
    } catch (err) {
      console.error('Error loading manifest:', err);
      setError('Failed to load icon data: ' + err.message);
      setLoadingManifest(false);
      setLoadingUsage(false);
    }
  }

  async function loadIconUsage() {
    try {
      const puzzlesSnapshot = await getDocs(collection(db, PUZZLES_COLLECTION));
      const puzzles = puzzlesSnapshot.docs.map(doc => doc.data());
      setIconUsage(checkIconUsage(puzzles));
    } catch (err) {
      console.error('Error loading icon usage:', err);
    } finally {
      setLoadingUsage(false);
    }
  }

  function handleFiles(files) {
    const validFiles = [];
    const iconNames = [];

    for (const file of files) {
      const validation = validateSvgFile(file);
      if (!validation.valid) {
        alert(`${file.name}: ${validation.error}`);
        continue;
      }
      validFiles.push(file);
      iconNames.push(file.name.replace(/\.svg$/i, '').toLowerCase());
    }

    if (validFiles.length === 0) return;
    setUploadDialog({ files: validFiles, iconNames: [...iconNames] });
  }

  async function performUpload() {
    if (!uploadDialog) return;

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < uploadDialog.files.length; i++) {
      const file = uploadDialog.files[i];
      const iconName = uploadDialog.iconNames[i];

      if (!isValidIconName(iconName)) {
        alert(`Invalid icon name "${iconName}". Use only lowercase letters, numbers, and underscores.`);
        errorCount++;
        continue;
      }

      if (manifest[iconName] && !window.confirm(`Icon "${iconName}" already exists. Replace it?`)) {
        continue;
      }

      try {
        await uploadIcon(file, iconName);
        successCount++;
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
        alert(`Failed to upload ${file.name}: ${err.message}`);
        errorCount++;
      }
    }

    setUploading(false);
    setUploadDialog(null);

    if (successCount > 0) {
      clearCustomIconsCache();
      alert(`Successfully uploaded ${successCount} icon(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
      await refreshList();
    }
  }

  function updateIconName(index, newName) {
    setUploadDialog(prev => ({
      ...prev,
      iconNames: prev.iconNames.map((name, i) => i === index ? newName : name)
    }));
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }

  function handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }

  function handleFileInput(e) {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  }

  async function handleReplace(iconName) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.svg';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const validation = validateSvgFile(file);
      if (!validation.valid) {
        alert(validation.error);
        return;
      }

      if (!window.confirm(`Replace "${iconName}" with "${file.name}"?`)) return;

      setUploading(true);
      try {
        await uploadIcon(file, iconName);
        clearCustomIconsCache();
        alert('Icon replaced successfully!');
        await refreshList();
      } catch (err) {
        alert('Failed to replace icon: ' + err.message);
      } finally {
        setUploading(false);
      }
    };
    input.click();
  }

  async function handleDelete(iconName) {
    // Treat icons as in-use while usage is still being checked
    if (loadingUsage) {
      alert('Cannot delete icon while usage is being checked. Please wait a moment.');
      return;
    }

    const usageCount = iconUsage[iconName] || 0;
    if (usageCount > 0) {
      alert(`Cannot delete "${iconName}". It is used in ${usageCount} puzzle(s).`);
      return;
    }

    if (!window.confirm(`Delete icon "${iconName}"? This cannot be undone.`)) return;

    setUploading(true);
    try {
      await deleteIcon(iconName);
      clearCustomIconsCache();
      alert('Icon deleted successfully!');
      await refreshList();
    } catch (err) {
      alert('Failed to delete icon: ' + err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleDownload() {
    if (iconNames.length === 0) {
      alert('No icons to download');
      return;
    }

    setDownloading(true);
    try {
      const zipBlob = await downloadAllIcons();
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'custom-icons.zip';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download icons: ' + err.message);
    } finally {
      setDownloading(false);
    }
  }

  function handleGeneratorClose() {
    setGeneratorOpen(false);
    // Don't reload data on close - only reload when icons are actually saved
  }

  function handleIconsSaved() {
    // Refresh icon list when icons are saved
    refreshList();
  }

  return (
    <div className="icons-manager">
      <h1>Custom Icons Manager</h1>

      <div className="upload-section">
        <div
          className={`drop-zone ${dragActive ? 'active' : ''}`}
          onDrop={handleDrop}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
        >
          <div className="drop-zone-content">
            <p className="drop-zone-text">
              {dragActive ? 'Drop SVG files here' : 'Drag & drop SVG files here'}
            </p>
            <p className="drop-zone-subtext">or</p>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <label className="file-input-label">
                <input
                  type="file"
                  accept=".svg"
                  multiple
                  onChange={handleFileInput}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
                <span className="btn-primary">Choose Files</span>
              </label>
              <button
                className="btn-secondary"
                onClick={() => setGeneratorOpen(true)}
                disabled={uploading}
                style={{ padding: '10px 20px', fontSize: '14px' }}
              >
                Create
              </button>
            </div>
            <p className="drop-zone-info">
              Max 100KB per file • Lowercase alphanumeric + underscores only
            </p>
          </div>
        </div>
      </div>

      <div className="icons-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>
            Current Icons{listLoaded ? ` (${iconNames.length})` : ''}
            {(uploading || loadingManifest) && <span className="loading-spinner"> Loading...</span>}
          </h2>
          {iconNames.length > 0 && (
            <button
              className="btn-secondary"
              onClick={handleDownload}
              disabled={downloading || loadingManifest}
              style={{ padding: '8px 16px', fontSize: '14px' }}
            >
              {downloading ? 'Downloading...' : 'Download All'}
            </button>
          )}
        </div>

        {!listLoaded ? (
          <button className="btn-secondary" onClick={loadData}>Load icons</button>
        ) : loadingManifest ? (
          <p className="empty-state">Loading icon manifest...</p>
        ) : iconNames.length === 0 ? (
          <p className="empty-state">No custom icons yet. Upload some to get started!</p>
        ) : (
          <>
          <input
            type="text"
            className="upload-name-input"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search icons..."
            style={{ maxWidth: '300px' }}
          />
          {filteredNames.length === 0 && (
            <p className="empty-state">No icons match "{search}".</p>
          )}
          <div className="icons-grid">
            {pageNames.map(iconName => (
              <div key={iconName} className="icon-card">
                <div className="icon-preview">
                  {iconUrls[iconName] ? (
                    <img
                      src={iconUrls[iconName]}
                      alt={iconName}
                      className="icon-svg"
                    />
                  ) : (
                    <div className="icon-placeholder">Loading...</div>
                  )}
                </div>
                <div className="icon-name" title={iconName}>
                  {iconName}
                </div>
                <div className="icon-info">
                  <span className="icon-version">{manifest[iconName]}</span>
                  {iconUsage[iconName] > 0 && (
                    <span className="icon-usage" title={`Used in ${iconUsage[iconName]} puzzle(s)`}>
                      Used: {iconUsage[iconName]}
                    </span>
                  )}
                </div>
                <div className="icon-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => handleReplace(iconName)}
                    disabled={uploading}
                  >
                    Replace
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => handleDelete(iconName)}
                    disabled={uploading || loadingUsage || (iconUsage[iconName] > 0)}
                    title={loadingUsage ? 'Checking usage...' : iconUsage[iconName] > 0 ? 'Cannot delete: in use' : 'Delete icon'}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="ic-pagination">
              <button className="btn-secondary" onClick={() => setPage(0)} disabled={currentPage === 0}>«</button>
              <button className="btn-secondary" onClick={() => setPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0}>‹ Prev</button>
              <span className="ic-page-info">Page {currentPage + 1} / {totalPages}</span>
              <button className="btn-secondary" onClick={() => setPage(Math.min(totalPages - 1, currentPage + 1))} disabled={currentPage >= totalPages - 1}>Next ›</button>
              <button className="btn-secondary" onClick={() => setPage(totalPages - 1)} disabled={currentPage >= totalPages - 1}>»</button>
            </div>
          )}
          </>
        )}
      </div>

      {/* Upload Dialog */}
      {uploadDialog && (
        <div className="modal-overlay" onClick={() => setUploadDialog(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Name Your Icons</h2>
            <p className="modal-description">
              Enter a name for each icon (lowercase, alphanumeric, and underscores only)
            </p>
            <div className="upload-items">
              {uploadDialog.files.map((file, index) => (
                <div key={index} className="upload-item">
                  <span className="upload-filename">{file.name}</span>
                  <input
                    type="text"
                    className="upload-name-input"
                    value={uploadDialog.iconNames[index]}
                    onChange={(e) => updateIconName(index, e.target.value)}
                    placeholder="icon_name"
                    autoFocus={index === 0}
                  />
                  {!isValidIconName(uploadDialog.iconNames[index]) && (
                    <span className="upload-error">Invalid name</span>
                  )}
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setUploadDialog(null)}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={performUpload}
                disabled={uploading || uploadDialog.iconNames.some(name => !isValidIconName(name))}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Icon Generator Sidebar */}
      <IconGenerator
        isOpen={generatorOpen}
        onClose={handleGeneratorClose}
        onIconsSaved={handleIconsSaved}
      />
    </div>
  );
}

export default CustomIconsManager;
